import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery } from 'urql';
import { assets } from '../../assets';
import Button from '../../components/buttons/button';
import { BlurredContainer, MattRow } from '../../components/containers/containers';
import DefaultScreen from '../../components/containers/default-screen';
import DataRow from '../../components/data-viewers/data-row/data-row';
import IDCard from '../../components/data-viewers/id-card';
import { formatDate, formatDateTime } from '../../helpers/date-formatters';
import { GraphqlOperations } from '../../logic/graphql/operations';
import {
  User,
  PersonalDocument,
  Query,
  Account,
  Address,
  TermsAndConditions,
  PersonalContract,
} from '../../logic/graphql/types/types';
import { Paths } from '../../navigation/paths';
import { withErrorBoundary } from '@sentry/react';
import { Fallback } from '../../components/utils/error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';
import config from '../../../config';

const CardTitleContainer = styled.title`
  height: 32px;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 30px;
`;

const CardTitle = styled.h2`
  margin-bottom: 0px;
`;

const TitleIcon = styled.img`
  height: 18px;
  width: 18px;
  object-fit: contain;
  margin-right: 18px;
`;

const AccountRow = styled(MattRow)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  margin-bottom: 30px;
  &:last-of-type {
    margin-bottom: 0px;
  }
`;

const PdfIcon = styled.img`
  width: 14px;
  height: 18px;
  margin-right: 8px;
`;

const LangLink = styled.b.attrs(() => ({}))<{ display: boolean }>`
  font-size: 12px;
  letter-spacing: -0.01em;
  margin-left: 10px;
  cursor: pointer;
  width: 20px;
  display: ${(props) => (props.display ? '' : 'none')};
`;

const AttachmentContainer = styled.section`
  padding: 20px 30px;
  background: var(--matt-lighter-bg-color);
  border-radius: 2px;
  width: 100%;
`;

const PersonalData = (): JSX.Element => {
  const intl = useIntl();
  const result = useQuery<Query>({
    query: GraphqlOperations.queries.getUserAllDetails.queryString,
    requestPolicy: 'network-only',
  })[0];
  const history = useHistory();

  const { data, fetching } = result;
  const user = data?.getUser;

  const handleEditClick = (): void => {
    history.push(Paths.contact);
  };

  const formatAddress = (addr: Address | undefined): string => {
    if (addr === undefined) {
      return '';
    }
    return addr.country.concat(' ', addr.zip_code, ' ', addr.city, ' ', addr.address);
  };

  const openAttahcment = (id: number, locale: string): void => {
    window.open(config.backend_url + '/termsofuse/' + id + '/' + locale, '_blank');
  };

  const renderUserContent = (user: User | undefined): JSX.Element => (
    <>
      <BlurredContainer>
        <CardTitleContainer>
          <TitleIcon src={assets.icons.personal_data} />
          <CardTitle>
            <FormattedMessage id="profilePage.personalData.personal.title" />
          </CardTitle>
        </CardTitleContainer>
        <DataRow
          label={intl.formatMessage({ id: 'profilePage.personalData.personal.name' })}
          value={user?.name || ''}
        />
        <DataRow
          label={intl.formatMessage({ id: 'profilePage.personalData.personal.birthPlace' })}
          value={user?.place_of_birth || ''}
        />
        <DataRow
          label={intl.formatMessage({ id: 'profilePage.personalData.personal.birthDate' })}
          value={formatDate(user?.time_of_birth) || ''}
        />
        <DataRow
          label={intl.formatMessage({ id: 'profilePage.personalData.personal.mothersName' })}
          value={user?.mothers_name || ''}
        />
        {user?.is_company === false ? (
          <DataRow
            label={intl.formatMessage({ id: 'profilePage.personalData.personal.taxNumber' })}
            value={user?.tax_number || ''}
          />
        ) : null}
      </BlurredContainer>
      {user?.is_company ? (
        <BlurredContainer>
          <CardTitleContainer>
            <TitleIcon src={assets.icons.personal_data_company} />
            <CardTitle>
              <FormattedMessage id="profilePage.personalData.company.title" />
            </CardTitle>
          </CardTitleContainer>
          <DataRow
            label={intl.formatMessage({ id: 'profilePage.personalData.company.companyName' })}
            value={user?.company_name || ''}
          />
          <DataRow
            label={intl.formatMessage({
              id: 'profilePage.personalData.company.registrationNumber',
            })}
            value={user?.registration_number || ''}
          />
          <DataRow
            label={intl.formatMessage({ id: 'profilePage.personalData.company.taxNumber' })}
            value={user?.tax_number || ''}
          />
        </BlurredContainer>
      ) : null}

      <BlurredContainer>
        <CardTitleContainer>
          <TitleIcon src={assets.icons.personal_data_billing} />
          <CardTitle>
            <FormattedMessage id="profilePage.personalData.notification.title" />
          </CardTitle>
        </CardTitleContainer>
        <DataRow
          label={intl.formatMessage({ id: 'profilePage.personalData.notification.phone' })}
          value={user?.phone || ''}
        />
        <DataRow
          label={intl.formatMessage({ id: 'profilePage.personalData.notification.email' })}
          value={user?.email || ''}
        />
        <DataRow
          label={intl.formatMessage({
            id: 'profilePage.personalData.notification.mailingAddress',
          })}
          value={formatAddress(user?.mailing_address)}
        />
        <DataRow
          label={intl.formatMessage({
            id: 'profilePage.personalData.notification.billingAddress',
          })}
          value={formatAddress(user?.billing_address)}
        />
      </BlurredContainer>
      <BlurredContainer>
        <CardTitleContainer>
          <TitleIcon src={assets.icons.personal_data_accounts} />
          <CardTitle>
            <FormattedMessage id="profilePage.personalData.accounts.title" />
          </CardTitle>
        </CardTitleContainer>
        {user?.accounts?.map((account: Account, index: number) => {
          return (
            <AccountRow key={index} className="flex flex-row justify-between mb-5">
              <span className="text-12">
                {account.type}: <b>{account.number}</b>
              </span>
            </AccountRow>
          );
        })}
      </BlurredContainer>
      {user?.personal_documents && user.personal_documents.length > 0 ? (
        <BlurredContainer>
          <CardTitleContainer>
            <TitleIcon src={assets.icons.personal_data_cards} />
            <CardTitle>
              <FormattedMessage id="profilePage.personalData.documents.title" />
            </CardTitle>
          </CardTitleContainer>
          <div className="flex flex-wrap">
            {user?.personal_documents?.map((doc: PersonalDocument, index: number) => (
              <div key={index} className="p-1">
                <IDCard
                  number={doc.number}
                  expiry={formatDate(doc.expiry)}
                  type={intl.locale === 'en' && doc.type_en !== '' ? doc.type_en : doc.type}
                  index={index}
                />
              </div>
            ))}
          </div>
        </BlurredContainer>
      ) : null}
      {user?.terms_and_conditions && user.terms_and_conditions.length > 0 ? (
        <BlurredContainer>
          <CardTitleContainer>
            <TitleIcon src={assets.icons.fa_solid_id_card} />
            <CardTitle>
              <FormattedMessage id="profilePage.personalData.terms_and_conditions.title" />
            </CardTitle>
          </CardTitleContainer>
          <div className="flex flex-wrap">
            {user?.terms_and_conditions?.map((term: TermsAndConditions, index: number) => (
              <div key={index} className="p-1 width-100">
                <AttachmentContainer>
                  <div key={index} className="flex flex-row items-center mb-4 width-100">
                    <PdfIcon src={assets.icons.pdf_icon} />
                    <span className="text-12" style={{ width: 300 }}>
                      <b>
                        {intl.locale === 'en' && term.name_en !== '' ? term.name_en : term.name}
                      </b>
                    </span>
                    <span
                      className="text-12 margin-10 cursor-pointer"
                      onClick={() => openAttahcment(term.id, 'hu')}
                    >
                      <b>hu</b>
                    </span>
                    <span
                      className="text-12 margin-10 cursor-pointer"
                      onClick={() => openAttahcment(term.id, 'en')}
                    >
                      <LangLink display={term.attachment_en !== ''}>en</LangLink>
                    </span>
                  </div>
                  <span className="text-12">{formatDateTime(term.time)}</span>
                </AttachmentContainer>
              </div>
            ))}
          </div>
        </BlurredContainer>
      ) : null}
      {user?.personal_contracts && user.personal_contracts.length > 0 ? (
        <BlurredContainer>
          <CardTitleContainer>
            <TitleIcon src={assets.icons.icon_park_solid_id_card} />
            <CardTitle>
              <FormattedMessage id="profilePage.personalData.contracts.title" />
            </CardTitle>
          </CardTitleContainer>
          <div className="flex flex-wrap">
            {user?.personal_contracts?.map((term: PersonalContract, index: number) => (
              <div key={index} className="p-1 width-100">
                <AttachmentContainer>
                  <div key={index} className="flex flex-row items-center mb-4 width-100">
                    <PdfIcon src={assets.icons.pdf_icon} />
                    <span className="text-12" style={{ width: 300 }}>
                      <b>
                        {intl.locale === 'en' && term.name_en !== '' ? term.name_en : term.name}
                      </b>
                    </span>
                    <span
                      className="text-12 margin-10 cursor-pointer"
                      onClick={() => openAttahcment(term.id, 'hu')}
                    >
                      <b>hu</b>
                    </span>
                    <span
                      className="text-12 margin-10 cursor-pointer"
                      onClick={() => openAttahcment(term.id, 'en')}
                    >
                      <LangLink display={term.attachment_en !== ''}>en</LangLink>
                    </span>
                  </div>
                  <span className="text-12">{formatDateTime(term.time)}</span>
                </AttachmentContainer>
              </div>
            ))}
          </div>
        </BlurredContainer>
      ) : null}
    </>
  );

  return (
    <DefaultScreen
      background={assets.images.profile_bg}
      title={intl.formatMessage({ id: 'profilePage.personalData.title' })}
      backUrl={Paths.profile}
      loading={fetching}
    >
      <div className="grid row-gap-1 col-gap-3  grid-cols-1 md:grid-cols-2 mb-6">
        {user ? renderUserContent(user) : null}
      </div>
      <Button onClick={handleEditClick}>
        <FormattedMessage id="profilePage.personalData.editButton" />
      </Button>
    </DefaultScreen>
  );
};

export default withErrorBoundary(PersonalData, { fallback: Fallback });
